// The preset catalogue (spec §10) — the rulesets the setup screen offers,
// and the module lists each one arms.
//
// Kept out of `ruleset.ts` so the contract (modules, tuning, seats) and the
// catalogue built on it can each be read on their own. The catalogue borrows
// the contract's primitives: `MODULE_IDS` for the module vocabulary,
// `sortedModules` for the canonical order, and `baseTuning`/`baseAssists` for
// the neutral values every preset starts from.

import {
  type AiProfile,
  type Assists,
  DARK_MODULES,
  MODULE_IDS,
  type ModuleId,
  type PresetId,
  type Ruleset,
  baseAssists,
  baseTuning,
  sortedModules,
} from './ruleset.js';

// ── Presets (spec §10) ─────────────────────────────────────────────────

export interface PresetDef {
  readonly id: Exclude<PresetId, 'custom'>;
  /** Suggested minimum age, e.g. 7 → "7+". */
  readonly minAge: number;
  /** Typical session length, minutes [min, max]. */
  readonly sessionMin: readonly [number, number];
  readonly ruleset: Ruleset;
  readonly humanAssists: Assists;
  readonly aiProfile: AiProfile;
}

const EXPLORER_MODULES: ModuleId[] = [
  'victory.milestones',
  'world.ruins',
  'heroes.enabled',
  'pacing.truce',
  'pacing.intensity',
];
const APPRENTICE_MODULES: ModuleId[] = [
  ...EXPLORER_MODULES,
  'world.fog_of_war',
  'diplomacy.basic',
  'economy.roads',
  'faith.enabled',
  'victory.conquest',
];
const STANDARD_MODULES: ModuleId[] = [
  ...APPRENTICE_MODULES,
  'doctrine.enabled',
  'magic.enabled',
  'science.research',
  'combat.formations',
  'combat.siege',
  'economy.upkeep',
  'economy.starvation',
  'world.neutrals',
  'heroes.loyalty',
  'economy.trade_routes',
  'economy.supply',
  'victory.economic',
  'victory.science',
  'victory.faith',
];

const ALL_BUT_MATURE: ModuleId[] = MODULE_IDS.filter(
  (m) => !m.startsWith('mature.') && !DARK_MODULES.includes(m),
);

export const PRESETS: readonly PresetDef[] = [
  {
    id: 'explorer',
    minAge: 7,
    sessionMin: [15, 25],
    ruleset: {
      v: 1,
      preset: 'explorer',
      modules: sortedModules(EXPLORER_MODULES),
      tuning: {
        ...baseTuning,
        pace: 'relaxed',
        mapSize: 'tiny',
        aiOpponents: 1,
        startingResources: 'high',
        victoryLength: 'short',
        peacePeriodMin: 8,
        cityCanBeDestroyed: false,
        truceMin: 4,
        intensityCeiling: 'low',
      },
      content: 'family',
    },
    humanAssists: {
      ...baseAssists,
      handicap: 'easy',
      autoManageCities: 'suggest',
      hints: 'always',
      pauseOnDanger: 'all',
      uiMode: 'icons',
      speedCap: 2,
    },
    aiProfile: 'gentle',
  },
  {
    id: 'apprentice',
    minAge: 10,
    sessionMin: [30, 40],
    ruleset: {
      v: 1,
      preset: 'apprentice',
      modules: sortedModules(APPRENTICE_MODULES),
      tuning: {
        ...baseTuning,
        pace: 'relaxed',
        mapSize: 'small',
        aiOpponents: 2,
        peacePeriodMin: 5,
        cityCanBeDestroyed: false,
        truceMin: 3,
        intensityCeiling: 'medium',
      },
      content: 'family',
    },
    humanAssists: { ...baseAssists, pauseOnDanger: 'all' },
    aiProfile: 'easy',
  },
  {
    id: 'standard',
    minAge: 13,
    sessionMin: [60, 90],
    ruleset: {
      v: 1,
      preset: 'standard',
      modules: sortedModules(STANDARD_MODULES),
      tuning: baseTuning,
      content: 'teen',
    },
    humanAssists: baseAssists,
    aiProfile: 'normal',
  },
  {
    id: 'strategist',
    minAge: 13,
    sessionMin: [90, 150],
    ruleset: {
      v: 1,
      preset: 'strategist',
      modules: sortedModules(ALL_BUT_MATURE),
      tuning: {
        ...baseTuning,
        mapSize: 'large',
        aiOpponents: 4,
        truceMin: 0.5,
        intensityCeiling: 'off',
      },
      content: 'teen',
    },
    humanAssists: { ...baseAssists, hints: 'off', pauseOnDanger: 'off', uiMode: 'expert' },
    aiProfile: 'hard',
  },
  {
    id: 'grand',
    minAge: 16,
    sessionMin: [120, 240],
    ruleset: {
      v: 1,
      preset: 'grand',
      modules: sortedModules(
        MODULE_IDS.filter(
          (m) => m !== 'pacing.truce' && m !== 'pacing.intensity' && !DARK_MODULES.includes(m),
        ),
      ),
      tuning: {
        ...baseTuning,
        pace: 'brisk',
        mapSize: 'large',
        aiOpponents: 6,
        truceMin: 0,
        intensityCeiling: 'off',
      },
      content: 'mature',
    },
    humanAssists: { ...baseAssists, hints: 'off', pauseOnDanger: 'off', uiMode: 'expert' },
    aiProfile: 'brutal',
  },
  {
    // Terrain-levels preset (terrain-levels-design.md): the only preset
    // that arms `terrain.levels` — levels 0..=12, high-ground combat,
    // climb costs, peaks uncapturable. The sea axis (sea-design §3 A8)
    // walls it in mountains: the cliff frames the levels, and a board
    // with no coast has no ports and no ships — naval is irrelevant at
    // altitude.
    id: 'highlands',
    minAge: 13,
    sessionMin: [75, 120],
    ruleset: {
      v: 1,
      preset: 'highlands',
      modules: sortedModules([...ALL_BUT_MATURE, 'terrain.levels']),
      tuning: {
        ...baseTuning,
        mapSize: 'large',
        aiOpponents: 3,
      },
      content: 'teen',
      sea: 'landlocked',
    },
    humanAssists: baseAssists,
    aiProfile: 'normal',
  },
  {
    // Champion preset (docs/champion-system-design.md): the only preset
    // that arms `legends.enabled` — one earned, once-per-game legendary
    // creature per player (Dragon in v1, summoned by a doctrine-gated
    // project). Never offered in the ranked queue.
    id: 'legends',
    minAge: 13,
    sessionMin: [75, 120],
    ruleset: {
      v: 1,
      preset: 'legends',
      modules: sortedModules([...ALL_BUT_MATURE, 'legends.enabled']),
      tuning: {
        ...baseTuning,
        mapSize: 'large',
        aiOpponents: 3,
      },
      content: 'teen',
    },
    humanAssists: baseAssists,
    aiProfile: 'normal',
  },
  {
    // Seafaring preset: the only one that arms `sea.trade` — player ships can
    // be built, sailed on water and worked for profit at harbours. Never
    // offered in the ranked queue.
    id: 'seafarers',
    minAge: 13,
    sessionMin: [75, 120],
    ruleset: {
      v: 1,
      preset: 'seafarers',
      modules: sortedModules([...ALL_BUT_MATURE, 'sea.trade']),
      tuning: {
        ...baseTuning,
        mapSize: 'large',
        aiOpponents: 3,
      },
      content: 'teen',
    },
    humanAssists: baseAssists,
    aiProfile: 'normal',
  },
  {
    // Royal Houses preset: arms `diplomacy.dynasty` — every seat has a court,
    // marriages bind alliances, and the AI weighs a marriage of its own — and
    // `diplomacy.generations` (armed 2026-11-01, the owner's call after the
    // chain was built dark: docs/generations-design.md §8 step 8): children
    // are born, heirs grow up, and a dead line ends in adoption — and the
    // espionage ops (armed 2026-11-02, the owner's call after design §12's
    // measure-first gate: docs/espionage-ops-design.md): sabotage,
    // assassination and supply poisoning (`espionage.ops`), the false-flag
    // frame (`espionage.false_flag`) and counter-intelligence
    // (`espionage.counter_intel`). Assassinate targets royals, which only
    // exist where courts exist — this is the theme match. The diplomacy
    // preset: the negotiation layer is on from the first tick. Never offered
    // in the ranked queue.
    id: 'royal-houses',
    minAge: 13,
    sessionMin: [75, 120],
    ruleset: {
      v: 1,
      preset: 'royal-houses',
      modules: sortedModules([
        ...ALL_BUT_MATURE,
        'diplomacy.dynasty',
        'diplomacy.generations',
        'espionage.ops',
        'espionage.false_flag',
        'espionage.counter_intel',
      ]),
      tuning: {
        ...baseTuning,
        mapSize: 'large',
        aiOpponents: 3,
      },
      content: 'teen',
    },
    humanAssists: baseAssists,
    aiProfile: 'normal',
  },
];

export function presetById(id: Exclude<PresetId, 'custom'>): PresetDef {
  const p = PRESETS.find((x) => x.id === id);
  if (!p) throw new Error(`unknown preset ${id}`);
  return p;
}
